import styled, { css } from "styled-components";

export const CommonTdStyle = css`
  position: relative;
  padding: var(--Spacing-12, 1.2rem) var(--Spacing-8, 0.8rem);
  line-height: 1.8rem;
  border: 1px solid var(--Color-Common-Border-Tertiary, #e5e7eb);
  font-size: 1.2rem;
  font-style: normal;
  font-weight: 400;
  line-height: 130%;
  vertical-align: top;
`;

export const CommonErrorStyles = css`
  background-color: ${({ theme }) => theme.color.neural.gray01};
  color: rgba(60, 71, 87, 0.5);

  a {
    color: rgba(60, 71, 87, 0.5);
  }

  span {
    color: rgba(60, 71, 87, 1);
  }
`;

export const PatentListTableBody = styled.tbody`
  background-color: #ffffff;
`;

export const PatentListTableTr = styled.tr<{
  isError: boolean;
}>`
  ${({ isError }) =>
    isError &&
    css`
      td {
        ${CommonErrorStyles}
      }
    `}
`;

export const PatentListTableId = styled.td`
  ${CommonTdStyle}
  width: 10rem;
  background: var(--Color-Common-Bg-Secondary, #f0f2f4);
  // NOTE: ID는 숫자라 줄바꿈 없이 한 줄로 표출 (break-all이면 auto 레이아웃에서 컬럼이 collapse됨)
  white-space: nowrap;
`;

export const PatentListTablePressId = styled.td<{
  $hasTooltip?: boolean;
}>`
  ${CommonTdStyle}
  width: 10rem;
  background: var(--Color-Common-Bg-Secondary, #f0f2f4);
  word-break: break-all;

  ${({ $hasTooltip }) =>
    $hasTooltip &&
    css`
      cursor: pointer;

      &:hover {
        background: #e5e7eb;

        .tooltip-wrapper {
          display: block;
        }
      }
    `}
`;

export const PatentListBulletList = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--Spacing-6, 0.6rem);
  margin: 0;
  padding: 0;
`;

export const PatentListBulletItem = styled.li`
  position: relative;
  padding-left: 0.7rem;
  list-style: none;
  word-break: break-all;
`;

export const PatentListBulletItemText = styled.span`
  &::before {
    position: absolute;
    left: 0rem;
    top: 0;
    display: block;
    content: "•";
    color: #2a2f37;
    font-size: 1.4rem;
  }
`;

export const PatentListNumber = styled.div``;

export const PatentListTooltipWrapper = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  margin-top: 0.2rem;
  z-index: 100;
  box-sizing: border-box;
  border-radius: var(--Border-Radius-4, 0.4rem);
  border: 1px solid var(--Color-Common-Border-Secondary, #b1b8c4);
  background: var(--Color-Common-Bg-Primary, #fff);
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.16),
    0px 0px 4px 0px rgba(0, 0, 0, 0.12);
  display: none;

  .tooltip {
    color: var(--Color-Common-Text-Primary, #2a2f37);
    font-size: 1.2rem;
    font-weight: 400;
    white-space: normal;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    display: flex;
    width: 100%;
    padding: var(--Spacing-12, 1.2rem) var(--Spacing-8, 0.8rem);
    gap: var(--Spacing-6, 0.6rem);
    border: none;
    word-break: break-all;
  }
`;

export const PatentListTableRegistryAgency = styled.td`
  ${CommonTdStyle}
  width: 8rem;
  word-break: break-all;
`;

export const PatentListTableLabelDate = styled.td`
  ${CommonTdStyle}
  width: 8rem;
  word-break: break-all;
`;

export const PatentListTableCompany = styled.td`
  ${CommonTdStyle}
  width: 10rem;
  min-width: 10rem;
  cursor: pointer;
  position: relative;
  word-break: break-all;

  &:hover {
    background: var(--Color-Common-Bg-Interactive-Primary-Subtle, #f0f9ff);

    .tooltip-wrapper {
      display: block;
    }
  }
`;

export const PatentListTableMcEditor = styled.td`
  ${CommonTdStyle}
  width: 8rem;
  word-break: break-all;
`;

export const PatentListTableFtEditor = styled.td`
  ${CommonTdStyle}
  width: 8rem;
  word-break: break-all;
`;

export const PatentListTableDate = styled.td`
  ${CommonTdStyle}
  width: 7.2rem;
`;

export const PatentListTableUnsuitable = styled.td<{
  isUnsuitable: boolean;
  isModalOpen?: boolean;
}>`
  ${CommonTdStyle}
  width: 12rem;
  background-color: ${({ isUnsuitable }) =>
    isUnsuitable ? "rgba(219, 15, 114, 0.1)" : ""};

  &:hover {
    background-color: var(--Color-Common-Bg-Warning-Subtle, #feece6);
  }

  &:active {
    background: var(--Color-Common-Bg-Danger-Subtle, #fde7e9);
  }

  ${({ isModalOpen }) =>
    isModalOpen &&
    css`
      background: var(--Color-Common-Bg-Warning-Subtle, #feece6);
    `}
`;

export const PatentListTableStatus = styled.td<{ code: string }>`
  ${CommonTdStyle}
  width: 7.2rem;
  text-align: center;
  vertical-align: middle;
`;

export const PatentListTableClassification = styled.td<{
  status: string;
}>`
  ${CommonTdStyle}
  width: 10rem;
  word-break: break-word;
  overflow-wrap: anywhere;
`;

export const PatentListTablePublicationNumber = styled.td`
  ${CommonTdStyle}
  width: 8rem;
  background: var(--Color-Common-Bg-Secondary, #f0f2f4);
  word-break: break-all;
`;

export const PatentListTablePublicationCountry = styled.td`
  ${CommonTdStyle}
  width: 4rem;
  word-break: break-all;
`;

export const PatentListTableApplicant = styled.td<{ $hasTooltip?: boolean }>`
  ${CommonTdStyle}
  width: 11rem;
  min-width: 11rem;
  word-break: break-word;
  overflow-wrap: anywhere;
  cursor: pointer;

  // NOTE: 클릭 시 디테일 페이지 이동 컬럼이라 호버 시 하늘색 처리
  &:hover {
    background: var(--Color-Common-Bg-Interactive-Primary-Subtle, #f0f9ff);
  }

  ${({ $hasTooltip }) =>
    $hasTooltip &&
    css`
      &:hover {
        .tooltip-wrapper {
          display: block;
        }
      }
    `}
`;

export const PatentListTableAssignee = styled.td<{ $hasTooltip?: boolean }>`
  ${CommonTdStyle}
  width: 11rem;
  min-width: 11rem;
  word-break: break-word;
  overflow-wrap: anywhere;
  cursor: pointer;

  // NOTE: 클릭 시 디테일 페이지 이동 컬럼이라 호버 시 하늘색 처리
  &:hover {
    background: var(--Color-Common-Bg-Interactive-Primary-Subtle, #f0f9ff);
  }

  ${({ $hasTooltip }) =>
    $hasTooltip &&
    css`
      &:hover {
        .tooltip-wrapper {
          display: block;
        }
      }
    `}
`;
